import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import countryNames from '../data/countryNames.json';
import CountryCombobox from '../components/CountryCombobox';
import SearchableSelect from '../components/SearchableSelect';
import { looksLikeCompleteEmail, isLikelyPersonalEmail, isLikelyProfessionalEmail } from '../utils/emailValidation';
import { MoreVerticalIcon, EyeIcon, MailIcon, ShieldIcon } from '../components/Icons';

const formatEventDate = (start, end, isMulti) => {
  if (!start) return '—';
  const s = new Date(start);
  if (!isMulti || !end) return s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  const e = new Date(end);
  if (s.toDateString() === e.toDateString()) return s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${s.getDate()} - ${e.getDate()} ${s.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;
  }
  return `${s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${e.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

const GUEST_TEMPLATE = () => ({
  guestTitle: 'Mr.',
  firstName: '',
  lastName: '',
  guestEmail: '',
  personalEmail: '',
  guestPhone: '',
  profession: '',
  organization: '',
  guestDepartment: '',
  industry: '',
  address: '',
  linkedinId: '',
  country: '',
});

const DEPARTMENT_OPTIONS = [
  'Administration',
  'Academics',
  'Research',
  'Student Affairs',
  'Training & Placement',
  'IT Department',
  'Finance',
  'Human Resources',
  'Operations',
  'Marketing & Communications',
  'Healthcare Services',
  'Legal Affairs',
  'Hospitality Services',
  'Media & Design',
  'Agriculture & Sustainability',
  'Other',
];

const INDUSTRY_OPTIONS = [
  'Information Technology',
  'Healthcare',
  'Education',
  'Finance & Banking',
  'Legal Services',
  'Hospitality & Tourism',
  'Media & Entertainment',
  'Construction & Architecture',
  'Manufacturing',
  'Agriculture & AgriTech',
  'Research & Development',
  'Government & Public Sector',
  'Consulting',
  'Retail & E-commerce',
  'Telecommunications',
  'Other',
];

function pickOrOther(value, options) {
  if (!value) return '';
  return options.includes(value) ? value : 'Other';
}

function socialOk(s) {
  const t = String(s || '').trim();
  return t.length >= 3;
}

function composeGuestName(guest) {
  return [guest.guestTitle, guest.firstName, guest.lastName].filter(Boolean).join(' ').trim();
}

function validateGuestsFront(list) {
  for (let i = 0; i < list.length; i++) {
    const g = list[i];
    const n = i + 1;
    const phone = String(g.guestPhone || '').replace(/\D/g, '');
    if (!g.guestTitle?.trim()) return `Guest ${n}: Title required`;
    if (!g.firstName?.trim()) return `Guest ${n}: First name required`;
    if (!looksLikeCompleteEmail(g.guestEmail)) return `Guest ${n}: Enter a valid work email`;
    if (!looksLikeCompleteEmail(g.personalEmail)) return `Guest ${n}: Enter a valid personal email`;
    if (String(g.guestEmail).trim().toLowerCase() === String(g.personalEmail).trim().toLowerCase()) return `Guest ${n}: Work and personal emails must differ`;

    if (isLikelyPersonalEmail(g.guestEmail)) return `Guest ${n}: Put your organization/work email here — ${g.guestEmail} looks personal`;
    if (isLikelyProfessionalEmail(g.personalEmail) && !isLikelyPersonalEmail(g.personalEmail)) {
      return `Guest ${n}: Personal email should be a typical personal inbox (Gmail, Yahoo, etc.), not ${g.personalEmail}`;
    }

    if (phone.length < 10 || phone.length > 15) return `Guest ${n}: Mobile must be numeric only (10–15 digits)`;
    if (!g.country || !countryNames.includes(g.country)) return `Guest ${n}: Choose country from suggestions (exact name)`;
    if (!g.profession?.trim()) return `Guest ${n}: Profession required`;
    if (!g.guestDepartment?.trim()) return `Guest ${n}: Department required`;
    if (!g.industry?.trim()) return `Guest ${n}: Industry required`;
    if (!g.address?.trim()) return `Guest ${n}: Address required`;
    if (!socialOk(g.linkedinId)) return `Guest ${n}: LinkedIn or Wikipedia URL required`;
  }
  return null;
}

function downloadPdfBlob(blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'visitor-passes.pdf';
  a.click();
  URL.revokeObjectURL(url);
}

const TicketPrintable = ({ pass }) => {
  const eventDateStr = formatEventDate(pass.event?.date, pass.event?.endDate, pass.event?.isMultiDay);
  return (
    <div id={`printable-pass-${pass._id}`} className="ticket-printable" style={{
      width: '420px',
      background: 'white',
      borderRadius: '20px',
      overflow: 'hidden',
      position: 'relative',
      border: '1px solid #e2e8f0',
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{ background: 'white', padding: '8px 24px', textAlign: 'center' }}>
        <img src={`${process.env.PUBLIC_URL}/logo.jpg`} alt="University Logo" style={{ maxHeight: '40px', width: 'auto', display: 'block', margin: '0 auto' }} />
      </div>
      <div style={{
        background: 'linear-gradient(135deg, #004e8a 0%, #005fa7 100%)',
        padding: '4px 20px 16px 20px',
        textAlign: 'center',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px'
      }}>
        <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.2em', opacity: 0.9, marginRight: '-0.2em', lineHeight: 1 }}>Official Access</div>
        <h1 style={{ fontSize: '18px', fontWeight: 800, letterSpacing: '0.05em', margin: 0, color: 'white', lineHeight: 1, marginRight: '-0.05em' }}>GUEST PASS</h1>
      </div>

      <div style={{ padding: '12px 24px 16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '40px', borderBottom: '2px dashed #e2e8f0', position: 'relative', background: '#fcfcfc' }}>
        <div style={{ padding: '4px', background: 'white', borderRadius: '12px', border: '1px solid #f1f5f9', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={pass.qrCodeData} alt="QR" style={{ width: '85px', height: '85px', display: 'block' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', marginTop: '-20px' }}>
          <div style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.05em', marginBottom: '-6px' }}>Entry Code</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '42px', fontWeight: 800, color: '#005fa7', lineHeight: 0.9, letterSpacing: '-0.02em' }}>{pass.passCode}</div>
        </div>
      </div>

      <div style={{ padding: '16px 24px' }}>
        {/* Visitor Information */}
        <div className="section-title-print" style={{ fontSize: '9px', fontWeight: 800, color: '#005fa7', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 8px 0', borderBottom: '1px solid #f1f5f9', paddingBottom: '3px', display: 'flex', alignItems: 'center' }}>
          Visitor Information
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', minHeight: '24px', width: '100%' }}>
            <div style={{ width: '100px', color: '#64748b', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', flexShrink: 0, display: 'flex', alignItems: 'center' }}>Full Name</div>
            <div style={{ flex: 1, fontWeight: 700, color: '#0f172a', fontSize: '15px', lineHeight: 1.2, display: 'flex', alignItems: 'center' }}>{pass.guestName}</div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', minHeight: '24px', width: '100%' }}>
            <div style={{ width: '100px', color: '#64748b', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', flexShrink: 0, display: 'flex', alignItems: 'center' }}>Mobile</div>
            <div style={{ flex: 1, fontWeight: 700, color: '#0f172a', fontSize: '14px', lineHeight: 1.2, display: 'flex', alignItems: 'center' }}>
              {(() => {
                const data = COUNTRY_PHONE_DATA[pass.country];
                const splitDigits = (s) => {
                  const d = s.replace(/\D/g, '');
                  if (d.length === 10) return `${d.slice(0, 5)} ${d.slice(5)}`;
                  if (d.length === 9) return `${d.slice(0, 4)} ${d.slice(4)}`;
                  return s;
                };

                if (data && pass.guestPhone) {
                  const codeStr = data.code.replace('+', '');
                  let cleanPhone = pass.guestPhone.replace(/\D/g, '');
                  if (cleanPhone.startsWith(codeStr)) {
                    cleanPhone = cleanPhone.slice(codeStr.length);
                  }
                  return `${data.code} ${splitDigits(cleanPhone)}`;
                }
                return splitDigits(pass.guestPhone) || '—';
              })()}
            </div>
          </div>
        </div>

        {/* Professional Details */}
        <div className="section-title-print" style={{ fontSize: '9px', fontWeight: 800, color: '#005fa7', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '4px 0 8px 0', borderBottom: '1px solid #f1f5f9', paddingBottom: '3px', display: 'flex', alignItems: 'center' }}>
          Professional Details
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', minHeight: '20px', width: '100%' }}>
            <div style={{ width: '100px', color: '#64748b', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', flexShrink: 0, display: 'flex', alignItems: 'center' }}>Profession</div>
            <div style={{ flex: 1, fontWeight: 700, color: '#0f172a', fontSize: '13px', lineHeight: 1.2, display: 'flex', alignItems: 'center' }}>{pass.profession}</div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', minHeight: '20px', width: '100%' }}>
            <div style={{ width: '100px', color: '#64748b', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', flexShrink: 0, display: 'flex', alignItems: 'center' }}>Organisation</div>
            <div style={{ flex: 1, fontWeight: 700, color: '#0f172a', fontSize: '13px', lineHeight: 1.2, display: 'flex', alignItems: 'center' }}>{pass.organization}</div>
          </div>
        </div>

        {/* Event Details */}
        <div className="section-title-print" style={{ fontSize: '9px', fontWeight: 800, color: '#005fa7', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '4px 0 8px 0', borderBottom: '1px solid #f1f5f9', paddingBottom: '3px', display: 'flex', alignItems: 'center' }}>
          Event Details
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', minHeight: '20px', width: '100%' }}>
            <div style={{ width: '100px', color: '#64748b', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', flexShrink: 0, display: 'flex', alignItems: 'center' }}>Coordinator</div>
            <div style={{ flex: 1, fontWeight: 700, color: '#0f172a', fontSize: '13px', lineHeight: 1.2, display: 'flex', alignItems: 'center' }}>{pass.coordinatorName || '—'}</div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', minHeight: '20px', width: '100%' }}>
            <div style={{ width: '100px', color: '#64748b', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', flexShrink: 0, display: 'flex', alignItems: 'center' }}>Contact</div>
            <div style={{ flex: 1, fontWeight: 700, color: '#0f172a', fontSize: '13px', lineHeight: 1.2, display: 'flex', alignItems: 'center' }}>{pass.event?.faculty?.phone || pass.issuedBy?.phone || '—'}</div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', minHeight: '20px', width: '100%' }}>
            <div style={{ width: '100px', color: '#64748b', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', flexShrink: 0, display: 'flex', alignItems: 'center' }}>Event</div>
            <div style={{ flex: 1, fontWeight: 800, color: '#005fa7', fontSize: '13px', lineHeight: 1.2, display: 'flex', alignItems: 'center' }}>{pass.event?.title}</div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', minHeight: '20px', width: '100%' }}>
            <div style={{ width: '100px', color: '#64748b', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', flexShrink: 0, display: 'flex', alignItems: 'center' }}>Venue</div>
            <div style={{ flex: 1, fontWeight: 700, color: '#0f172a', fontSize: '11.5px', lineHeight: 1.2, display: 'flex', alignItems: 'center' }}>{pass.event?.venue}, KRMU</div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', minHeight: '20px', width: '100%' }}>
            <div style={{ width: '100px', color: '#64748b', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', flexShrink: 0, display: 'flex', alignItems: 'center' }}>Date</div>
            <div style={{ flex: 1, fontWeight: 700, color: '#0f172a', fontSize: '13px', lineHeight: 1.2, display: 'flex', alignItems: 'center' }}>{eventDateStr}</div>
          </div>
        </div>
      </div>

      <div style={{ background: '#f0fdf4', padding: '16px 24px', textAlign: 'center', borderTop: '1px solid #dcfce7' }}>
        <div style={{ fontSize: '11px', fontWeight: 600, color: '#166534' }}>Valid on event date. Present at gate with physical ID for verification.</div>
      </div>
    </div>
  );
};

const downloadPassesAsPDF = async (passes) => {
  if (!passes || passes.length === 0) return;
  const toastId = toast.loading(`Generating ${passes.length === 1 ? 'pass' : 'passes'}...`);

  try {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'px',
    });

    for (let i = 0; i < passes.length; i++) {
      const pass = passes[i];
      const element = document.getElementById(`printable-pass-${pass._id}`);
      if (!element) continue;

      // Ensure images (QR code) are loaded
      const images = element.getElementsByTagName('img');
      await Promise.all(Array.from(images).map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise(resolve => { img.onload = resolve; img.onerror = resolve; });
      }));

      const canvas = await html2canvas(element, {
        scale: 3,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      const w = canvas.width / 3;
      const h = canvas.height / 3;

      if (i > 0) pdf.addPage([w, h], 'portrait');
      else pdf.deletePage(1), pdf.addPage([w, h], 'portrait'); // Set first page size

      pdf.addImage(imgData, 'PNG', 0, 0, w, h);
    }

    const fileName = passes.length === 1
      ? `VisitorPass_${passes[0].guestName.replace(/\s+/g, '_')}_${passes[0].passCode}.pdf`
      : `VisitorPasses_${new Date().getTime()}.pdf`;

    pdf.save(fileName);
    toast.success('Download complete!', { id: toastId });
  } catch (err) {
    console.error('PDF Generation failed:', err);
    toast.error('Failed to generate PDF.', { id: toastId });
  }
};

function inputWarnStyle(show) {
  return show ? { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(220,38,38,0.14)' } : undefined;
}

function inputInfoStyle(show) {
  return show ? { borderColor: 'var(--amber)', boxShadow: '0 0 0 3px rgba(217,119,6,0.15)' } : undefined;
}

export const COUNTRY_PHONE_DATA = {
  "India": { code: "+91", length: 10 },
  "United States": { code: "+1", length: 10 },
  "United Kingdom": { code: "+44", length: 10 },
  "United Arab Emirates": { code: "+971", length: 9 },
  "Singapore": { code: "+65", length: 8 },
  "Australia": { code: "+61", length: 9 },
  "Canada": { code: "+1", length: 10 },
  "Germany": { code: "+49", length: 11 },
  "France": { code: "+33", length: 9 },
  "Japan": { code: "+81", length: 10 },
  "China": { code: "+86", length: 11 },
  "Brazil": { code: "+55", length: 11 },
  "Russia": { code: "+7", length: 10 },
  "South Africa": { code: "+27", length: 9 },
  "Netherlands": { code: "+31", length: 9 },
  "Switzerland": { code: "+41", length: 9 },
  "Sweden": { code: "+46", length: 9 },
  "Norway": { code: "+47", length: 8 },
  "Denmark": { code: "+45", length: 8 },
  "Italy": { code: "+39", length: 10 },
  "Spain": { code: "+34", length: 9 },
  "Mexico": { code: "+52", length: 10 },
  "Saudi Arabia": { code: "+966", length: 9 },
  "Qatar": { code: "+974", length: 8 },
  "Oman": { code: "+968", length: 8 },
  "Kuwait": { code: "+965", length: 8 },
  "Bahrain": { code: "+973", length: 8 },
  "Afghanistan": { code: "+93", length: 9 },
  "Albania": { code: "+355", length: 9 },
  "Algeria": { code: "+213", length: 9 },
  "Argentina": { code: "+54", length: 10 },
  "Austria": { code: "+43", length: 10 },
  "Bangladesh": { code: "+880", length: 10 },
  "Belgium": { code: "+32", length: 9 },
  "Chile": { code: "+56", length: 9 },
  "Colombia": { code: "+57", length: 10 },
  "Egypt": { code: "+20", length: 10 },
  "Finland": { code: "+358", length: 9 },
  "Greece": { code: "+30", length: 10 },
  "Hong Kong": { code: "+852", length: 8 },
  "Indonesia": { code: "+62", length: 10 },
  "Iran": { code: "+98", length: 10 },
  "Ireland": { code: "+353", length: 9 },
  "Israel": { code: "+972", length: 9 },
  "Malaysia": { code: "+60", length: 9 },
  "New Zealand": { code: "+64", length: 9 },
  "Nigeria": { code: "+234", length: 10 },
  "Pakistan": { code: "+92", length: 10 },
  "Philippines": { code: "+63", length: 10 },
  "Poland": { code: "+48", length: 9 },
  "Portugal": { code: "+351", length: 9 },
  "South Korea": { code: "+82", length: 10 },
  "Sri Lanka": { code: "+94", length: 9 },
  "Thailand": { code: "+66", length: 9 },
  "Turkey": { code: "+90", length: 10 },
  "Vietnam": { code: "+84", length: 9 },
};

export function IssuePassModal({ events, onClose, onIssued }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(1);
  const [eventId, setEventId] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [guests, setGuests] = useState([GUEST_TEMPLATE()]);
  const [issuedPasses, setIssuedPasses] = useState([]);
  const [eventSearch, setEventSearch] = useState('');

  const filteredEvents = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    return events
      .filter((ev) => {
        const evStart = new Date(ev.date);
        evStart.setHours(0, 0, 0, 0);
        const evEnd = ev.isMultiDay && ev.endDate ? new Date(ev.endDate) : evStart;
        evEnd.setHours(0, 0, 0, 0);

        if (evEnd < now) return false;

        if (eventSearch.trim()) {
          const s = eventSearch.toLowerCase();
          return ev.title.toLowerCase().includes(s) || ev.venue?.toLowerCase().includes(s);
        }
        return true;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [events, eventSearch]);

  const handleCountryChange = (idx, country) => {
    const newData = COUNTRY_PHONE_DATA[country];
    setGuests((prev) => {
      const next = [...prev];
      const g = next[idx];
      const oldData = COUNTRY_PHONE_DATA[g.country];
      let currentPhone = g.guestPhone || '';
      let newPhone = currentPhone;

      if (newData) {
        if (!currentPhone.trim() || currentPhone.trim() === '+') {
          newPhone = newData.code + ' ';
        } else if (oldData && currentPhone.startsWith(oldData.code)) {
          newPhone = newData.code + ' ' + currentPhone.slice(oldData.code.length).trim();
        } else {
          const foundOld = Object.values(COUNTRY_PHONE_DATA).find(d => currentPhone.startsWith(d.code));
          if (foundOld) {
            newPhone = newData.code + ' ' + currentPhone.slice(foundOld.code.length).trim();
          }
        }
      }

      next[idx] = { ...g, country, guestPhone: newPhone };
      return next;
    });
  };

  const selEvent = events.find((e) => e._id === eventId);

  const setGuest = (idx, key, val) => {
    setGuests((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: val };
      return next;
    });
  };

  const handleCountNext = () => {
    const n = Math.min(100, Math.max(1, parseInt(String(count), 10) || 1));
    const ev = events.find((e) => e._id === eventId);
    if (!ev) return toast.error('Select an event');

    // Default sendEmail to true for Offline/Hybrid unless event-level setting says otherwise
    const shouldDefaultSend = ev.eventMode !== 'online' && ev.generatePass !== false;
    setSendEmail(shouldDefaultSend);

    setCount(n);
    setGuests(Array.from({ length: n }, () => GUEST_TEMPLATE()));
    setStep(3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const prepared = guests.map((g) => ({
      ...g,
      guestName: composeGuestName(g),
      guestPhone: String(g.guestPhone || '').replace(/\D/g, ''),
    }));

    const err = validateGuestsFront(prepared);
    if (err) {
      toast.error(err);
      return;
    }

    setLoading(true);
    try {
      const shouldGenPass = selEvent && selEvent.eventMode !== 'online' && selEvent.generatePass !== false;
      const finalSendEmail = shouldGenPass ? sendEmail : false;

      const res = await api.post('/passes/bulk', { eventId, sendEmail: finalSendEmail, guests: prepared });
      const newPasses = res.data.passes || [];
      setIssuedPasses(newPasses);
      toast.success(`${newPasses.length} pass(es) issued`);

      // Skip download and just signal completion
      onIssued();
    } catch (err3) {
      toast.error(err3.response?.data?.message || 'Failed to issue passes');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" role="presentation">
      <div className="modal" style={{ maxWidth: '44rem' }}>
        <div className="modal-header">
          <div className="modal-title">Issue visitor passes</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="issue-steps">
          {['Event', 'How many', 'Guest details'].map((s, i) => (
            <div key={s} className="issue-step-col">
              <div className={`issue-step-bar ${step > i ? 'is-done' : ''}`} />
              <div className={`issue-step-label ${step > i ? 'is-done' : ''}`}>{s}</div>
            </div>
          ))}
        </div>

        {/* Hidden Printable Templates for Bulk Download */}
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          {issuedPasses.map(p => <TicketPrintable key={p._id} pass={p} />)}
        </div>

        <form onSubmit={handleSubmit} autoComplete="off" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
          <div className="modal-body">
            {step === 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label htmlFor="ip-event-search" style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', marginBottom: '0.5rem', display: 'block' }}>Search Upcoming Events</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="ip-event-search"
                      type="text"
                      placeholder="Type event name or venue..."
                      value={eventSearch}
                      onChange={(e) => setEventSearch(e.target.value)}
                      style={{ paddingLeft: '2.75rem', height: '3.25rem', fontSize: '0.9375rem', borderRadius: '12px', border: '2px solid #e2e8f0', background: 'white' }}
                    />
                    <div style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                    </div>
                  </div>
                </div>

                <div style={{
                  maxHeight: '320px',
                  overflowY: 'auto',
                  border: '1px solid #e2e8f0',
                  borderRadius: '16px',
                  background: '#f8fafc',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  padding: '4px'
                }}>
                  {filteredEvents.length === 0 ? (
                    <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#94a3b8' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', marginBottom: '0.5rem', letterSpacing: '0.05em' }}>No Events Found</div>
                      <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Try searching for a different name or venue.</div>
                    </div>
                  ) : (
                    filteredEvents.map(ev => {
                      const isSelected = eventId === ev._id;
                      const d = new Date(ev.date);
                      return (
                        <div
                          key={ev._id}
                          onClick={() => setEventId(ev._id)}
                          style={{
                            padding: '0.875rem 1rem',
                            background: isSelected ? '#eff6ff' : 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem',
                            borderRadius: '12px',
                            border: isSelected ? '2px solid #005fa7' : '2px solid transparent',
                            boxShadow: isSelected ? '0 4px 6px -1px rgba(0, 95, 167, 0.1), 0 2px 4px -1px rgba(0, 95, 167, 0.06)' : 'none',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        >
                          <div style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '10px',
                            background: isSelected ? '#005fa7' : '#f1f5f9',
                            color: isSelected ? 'white' : '#64748b',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            boxShadow: isSelected ? '0 2px 4px rgba(0, 95, 167, 0.2)' : 'none'
                          }}>
                            <div style={{ fontSize: '16px', fontWeight: 800, lineHeight: 1 }}>{d.getDate()}</div>
                            <div style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', marginTop: '1px' }}>{d.toLocaleDateString('en-US', { month: 'short' })}</div>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: isSelected ? '#004e8a' : '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.title}</div>
                            <div style={{ fontSize: '0.8125rem', color: isSelected ? '#2563eb' : '#64748b', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                              {ev.venue}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: isSelected ? '#005fa7' : '#94a3b8', letterSpacing: '0.04em' }}>{ev.passCount || 0} issued</div>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            )}

            {step === 3 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {selEvent && (
                  <div style={{ width: '100%', background: '#f8fafc', padding: '0.875rem 1.125rem', borderRadius: '14px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                    <div style={{ width: '38px', height: '38px', borderRadius: '8px', background: '#005fa7', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 4px rgba(0,95,167,0.1)' }}>
                      <div style={{ fontSize: '14px', fontWeight: 800, lineHeight: 1 }}>{new Date(selEvent.date).getDate()}</div>
                      <div style={{ fontSize: '8px', fontWeight: 700, textTransform: 'uppercase' }}>{new Date(selEvent.date).toLocaleDateString('en-US', { month: 'short' })}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: '0.9375rem', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selEvent.title}</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>{count} guest(s) arriving</div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.375rem' }}>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setStep(1)} style={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', color: '#005fa7' }}>Change Event</button>
                    </div>
                  </div>
                )}

                {selEvent && (selEvent.eventMode === 'online' || selEvent.generatePass === false) && (
                  <div style={{
                    padding: '0.875rem 1.125rem',
                    background: '#fff7ed',
                    borderRadius: '12px',
                    border: '1px solid #ffedd5',
                    display: 'flex',
                    gap: '0.75rem',
                    alignItems: 'center',
                    marginBottom: '1rem'
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9a3412" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                    <div style={{ fontSize: '0.8125rem', color: '#9a3412', fontWeight: 600 }}>
                      This event is <strong>{selEvent.eventMode === 'online' ? 'Online' : 'configured to not issue passes'}</strong>.
                      Guest info will be saved, but no entry passes will be sent.
                    </div>
                  </div>
                )}

                {selEvent && selEvent.eventMode === 'hybrid' && (
                  <div style={{
                    padding: '1rem 1.25rem',
                    background: '#f0fdf4',
                    borderRadius: '12px',
                    border: '1px solid #dcfce7',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                      <div style={{ fontSize: '0.8125rem', color: '#15803d', fontWeight: 700 }}>Generate and send entry passes to these guests?</div>
                    </div>
                    <select
                      value={sendEmail ? 'yes' : 'no'}
                      onChange={(ev) => setSendEmail(ev.target.value === 'yes')}
                      style={{ padding: '0.4rem 0.75rem', borderRadius: '8px', border: '1px solid #b9f6ca', fontSize: '0.75rem', fontWeight: 700, background: 'white' }}
                    >
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', overflowY: 'auto', flex: 1 }}>
                  {guests.map((g, i) => {
                    const profPersonal = looksLikeCompleteEmail(g.guestEmail) && isLikelyPersonalEmail(g.guestEmail);
                    const persCorporate =
                      looksLikeCompleteEmail(g.personalEmail) &&
                      !isLikelyPersonalEmail(g.personalEmail) &&
                      isLikelyProfessionalEmail(g.personalEmail);

                    const liWarn = g.linkedinId && String(g.linkedinId).trim() && !socialOk(g.linkedinId);
                    const deptPick = pickOrOther(g.guestDepartment, DEPARTMENT_OPTIONS);
                    const industryPick = pickOrOther(g.industry, INDUSTRY_OPTIONS);

                    const isSameEmail = g.guestEmail && g.personalEmail && g.guestEmail.trim().toLowerCase() === g.personalEmail.trim().toLowerCase();
                    const phoneDigits = (g.guestPhone || '').replace(/\D/g, '');
                    const countryData = COUNTRY_PHONE_DATA[g.country] || { code: '', length: 10 };
                    const prefixDigits = countryData.code.replace(/\D/g, '');
                    const actualDigits = (prefixDigits && phoneDigits.startsWith(prefixDigits))
                      ? phoneDigits.slice(prefixDigits.length)
                      : phoneDigits;
                    const isPhoneShort = actualDigits.length > 0 && actualDigits.length < countryData.length;
                    const hasError = profPersonal || isSameEmail || isPhoneShort;

                    return (
                      <div key={i} className="section-card" style={{
                        padding: '1.5rem',
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                        marginBottom: '1rem',
                        transition: 'all 0.2s'
                      }}>
                        <div style={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '1.5rem', color: '#005fa7', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                          <div style={{ width: '22px', height: '22px', borderRadius: '6px', background: '#eff6ff', color: '#005fa7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', border: '1px solid #dbeafe' }}>{i + 1}</div>
                          Guest Details
                        </div>

                        {/* Name Row: Title, First, Last */}
                        <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
                          <div className="field" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.04em' }}>Title *</label>
                            <select required value={g.guestTitle} onChange={(ev) => setGuest(i, 'guestTitle', ev.target.value)} style={{ borderRadius: '12px', height: '3.25rem' }}>
                              {['Mr.', 'Ms.', 'Mrs.', 'Dr.', 'Prof.', 'Mx.'].map((t) => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </div>
                          <div className="field" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.04em' }}>First name *</label>
                            <input required name={`fname-${i}`} autoComplete="none" value={g.firstName} onChange={(ev) => setGuest(i, 'firstName', ev.target.value)} placeholder="John" style={{ borderRadius: '12px', height: '3.25rem' }} />
                          </div>
                          <div className="field" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.04em' }}>Last name</label>
                            <input name={`lname-${i}`} autoComplete="none" value={g.lastName} onChange={(ev) => setGuest(i, 'lastName', ev.target.value)} placeholder="Doe" style={{ borderRadius: '12px', height: '3.25rem' }} />
                          </div>
                        </div>

                        {/* Email Row: Work, Personal */}
                        <div className="grid-2" style={{ gap: '1rem', marginBottom: '1.25rem' }}>
                          <div className="field" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.04em' }}>Work / professional email *</label>
                            <input
                              required
                              type="email"
                              name={`wemail-${i}`}
                              autoComplete="none"
                              placeholder="you@company.com"
                              style={{ ...inputWarnStyle(profPersonal), borderRadius: '12px', height: '3.25rem' }}
                              value={g.guestEmail}
                              onChange={(ev) => setGuest(i, 'guestEmail', ev.target.value)}
                            />
                            {profPersonal && (
                              <span className="field-error-msg" style={{ fontSize: '0.65rem' }}>Use company email</span>
                            )}
                          </div>
                          <div className="field" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.04em' }}>Personal email *</label>
                            <input
                              required
                              type="email"
                              name={`pemail-${i}`}
                              autoComplete="none"
                              placeholder="you@gmail.com"
                              style={{ ...inputWarnStyle(persCorporate || isSameEmail), borderRadius: '12px', height: '3.25rem' }}
                              value={g.personalEmail}
                              onChange={(ev) => setGuest(i, 'personalEmail', ev.target.value)}
                            />
                            {(persCorporate || isSameEmail) && (
                              <span className="field-error-msg" style={{ fontSize: '0.65rem' }}>{isSameEmail ? 'Must differ from work email' : 'Move work email up'}</span>
                            )}
                          </div>
                        </div>

                        {/* Country & Mobile Row */}
                        <div className="grid-2" style={{ gap: '1rem', marginBottom: '1.25rem' }}>
                          <div style={{ marginBottom: 0 }}>
                            <CountryCombobox
                              id={`guest-${i}-country`}
                              label="Country"
                              required
                              value={g.country}
                              onChange={(val) => handleCountryChange(i, val)}
                            />
                          </div>
                          <div className="field" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.04em' }}>Mobile * (digits only)</label>
                            <input
                              required
                              name={`phone-${i}`}
                              autoComplete="none"
                              inputMode="numeric"
                              placeholder="e.g. +91 9876543210"
                              style={{ ...inputWarnStyle(isPhoneShort), borderRadius: '12px', height: '3.25rem' }}
                              value={g.guestPhone}
                              onChange={(ev) => {
                                const val = ev.target.value;
                                const data = COUNTRY_PHONE_DATA[g.country] || { code: '+', length: 15 };
                                let clean = val.replace(/[^\d+ ]/g, '');
                                if (clean && !clean.startsWith('+')) clean = '+' + clean;
                                if (data.code && clean.startsWith(data.code) && clean.length > data.code.length && clean[data.code.length] !== ' ') {
                                  clean = data.code + ' ' + clean.substring(data.code.length);
                                }
                                const maxAllowed = data.code.length + 1 + data.length;
                                if (clean.length > maxAllowed) return;
                                setGuest(i, 'guestPhone', clean);
                              }}
                            />
                            {isPhoneShort && <span className="field-error-msg" style={{ fontSize: '0.65rem' }}>Minimum {countryData.length} digits</span>}
                          </div>
                        </div>

                        <div className="grid-2" style={{ gap: '1rem', marginBottom: '1.25rem' }}>
                          <div className="field" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.04em' }}>Designation *</label>
                            <input required name={`prof-${i}`} autoComplete="none" value={g.profession} onChange={(ev) => setGuest(i, 'profession', ev.target.value)} placeholder="e.g. HR, UX Designer" style={{ borderRadius: '12px', height: '3.25rem' }} />
                          </div>
                          <div className="field" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.04em' }}>Organization</label>
                            <input name={`org-${i}`} autoComplete="none" value={g.organization} onChange={(ev) => setGuest(i, 'organization', ev.target.value)} placeholder="e.g. Ernst & Young" style={{ borderRadius: '12px', height: '3.25rem' }} />
                          </div>
                        </div>

                        <div className="grid-2" style={{ gap: '1rem', marginBottom: '1.25rem' }}>
                          <div className="field" style={{ marginBottom: 0 }}>
                            <SearchableSelect
                              id={`guest-${i}-dept`}
                              label="Department"
                              required
                              options={DEPARTMENT_OPTIONS}
                              value={deptPick}
                              onChange={(val) => setGuest(i, 'guestDepartment', val)}
                              placeholder="Select department..."
                            />
                            {deptPick === 'Other' && (
                              <input
                                required
                                name={`dept-other-${i}`}
                                autoComplete="none"
                                value={g.guestDepartment === 'Other' ? '' : g.guestDepartment}
                                onChange={(ev) => setGuest(i, 'guestDepartment', ev.target.value)}
                                placeholder="Specify department"
                                style={{ marginTop: '0.5rem', borderRadius: '12px', height: '3.25rem' }}
                              />
                            )}
                          </div>
                          <div className="field" style={{ marginBottom: 0 }}>
                            <SearchableSelect
                              id={`guest-${i}-industry`}
                              label="Industry"
                              required
                              options={INDUSTRY_OPTIONS}
                              value={industryPick}
                              onChange={(val) => setGuest(i, 'industry', val)}
                              placeholder="Select industry..."
                            />
                            {industryPick === 'Other' && (
                              <input
                                required
                                name={`ind-other-${i}`}
                                autoComplete="none"
                                value={g.industry === 'Other' ? '' : g.industry}
                                onChange={(ev) => setGuest(i, 'industry', ev.target.value)}
                                placeholder="Specify industry"
                                style={{ marginTop: '0.5rem', borderRadius: '12px', height: '3.25rem' }}
                              />
                            )}
                          </div>
                        </div>
                        <div className="field" style={{ marginBottom: '1.25rem' }}>
                          <label style={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.04em' }}>Office Address *</label>
                          <textarea
                            required
                            name={`addr-${i}`}
                            autoComplete="none"
                            rows={2}
                            placeholder="Street, city, state, postal code"
                            value={g.address}
                            onChange={(ev) => setGuest(i, 'address', ev.target.value)}
                            style={{ borderRadius: '12px', padding: '0.75rem 1rem', minHeight: '80px' }}
                          />
                        </div>
                        <div className="field" style={{ marginBottom: 0 }}>
                          <label style={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.04em' }}>LinkedIn / Wikipedia Profile *</label>
                          <input
                            required
                            name={`li-${i}`}
                            autoComplete="none"
                            placeholder="linkedin.com/in/... or wikipedia.org/..."
                            style={{ ...inputWarnStyle(liWarn), borderRadius: '12px', height: '3.25rem' }}
                            value={g.linkedinId}
                            onChange={(ev) => setGuest(i, 'linkedinId', ev.target.value)}
                          />
                          {liWarn && <span className="field-error-msg">Must include linkedin.com or lnkd.in</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: '1rem', padding: '1rem', background: '#f0f9ff', borderRadius: '12px', border: '1px solid #e0f2fe', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#0ea5e9' }} />
                  <div style={{ fontSize: '0.75rem', color: '#0369a1', fontWeight: 600 }}>Guests will be added to the event guest list immediately.</div>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            {step === 1 && (
              <button type="button" className="btn btn-navy" onClick={() => (eventId ? setStep(3) : toast.error('Select an event'))}>
                Next
              </button>
            )}
            {step === 2 && (
              <button type="button" className="btn btn-navy" onClick={handleCountNext}>
                Next
              </button>
            )}
            {step === 3 && (
              <button type="submit" className="btn btn-navy" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner" /> Issuing…
                  </>
                ) : (
                  'Issue passes'
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export function RevokePassModal({ pass, onClose, onRevoke }) {
  const [reason, setReason] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onRevoke(pass._id, reason);
    setLoading(false);
  };

  return (
    <div className="modal-overlay" role="presentation">
      <div className="modal" style={{ maxWidth: '34rem' }}>
        <div className="modal-header">
          <div className="modal-title" style={{ color: '#ef4444' }}>Revoke Guest Pass</div>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ marginBottom: '0.5rem' }}>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-2)', marginBottom: '1rem', lineHeight: '1.5' }}>
                You are about to revoke the pass for <strong>{pass.guestName}</strong>. This action is permanent and will be logged.
              </div>
              <div style={{
                padding: '1rem',
                background: '#fff1f2',
                border: '1px solid #ffe4e6',
                borderRadius: 'var(--radius)',
                fontSize: 'var(--text-xs)',
                color: '#9f1239',
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'flex-start',
                lineHeight: '1.4'
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                <span>Revoked passes will immediately become invalid for entry at the gate security.</span>
              </div>
            </div>

            <div className="field">
              <label htmlFor="revocation-reason">Reason for Revocation</label>
              <textarea
                id="revocation-reason"
                style={{ minHeight: '120px' }}
                placeholder="Please explain why this pass is being revoked..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                autoFocus
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={loading}>
              Keep Pass
            </button>
            <button type="submit" className="btn" style={{ background: '#ef4444', color: '#fff', borderColor: '#ef4444' }} disabled={loading || !reason.trim()}>
              {loading ? <span className="spinner" /> : 'Confirm Revocation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function PassDetailModal({ pass, onClose, onResend, onRevoke, readOnly }) {
  const displayName = pass.guestName || composeGuestName(pass) || '—';
  const eventDateStr = formatEventDate(pass.event?.date, pass.event?.endDate, pass.event?.isMultiDay);

  const downloadPassAsPDF = async (pass) => {
    downloadPassesAsPDF([pass]);
  };

  const formatPhone = (phone, country) => {
    if (!phone) return '—';
    const data = COUNTRY_PHONE_DATA[country];
    const splitDigits = (s) => {
      const d = s.replace(/\D/g, '');
      if (d.length === 10) return `${d.slice(0, 5)} ${d.slice(5)}`;
      if (d.length === 9) return `${d.slice(0, 4)} ${d.slice(4)}`;
      return s;
    };
    if (data) {
      const codeStr = data.code.replace('+', '');
      let cleanPhone = phone.replace(/\D/g, '');
      if (cleanPhone.startsWith(codeStr)) {
        cleanPhone = cleanPhone.slice(codeStr.length);
      }
      return `${data.code} ${splitDigits(cleanPhone)}`;
    }
    return splitDigits(phone);
  };

  const formatLinkedIn = (id) => {
    if (!id) return null;
    let clean = id.trim();
    if (clean.startsWith('http')) return clean;
    if (clean.startsWith('linkedin.com')) return `https://${clean}`;
    if (clean.startsWith('en.wikipedia.org') || clean.startsWith('wikipedia.org')) return `https://${clean}`;
    if (clean.includes('wikipedia.org')) return clean.startsWith('http') ? clean : `https://${clean}`;
    return `https://www.linkedin.com/in/${clean}`;
  };

  const DetailItem = ({ label, value }) => (
    <div className="pass-grid-col">
      <span className="pass-grid-label">{label}</span>
      <span className="pass-grid-value">{value || '—'}</span>
    </div>
  );

  const shouldGenPass = pass.isPassGenerated !== false && pass.event?.eventMode !== 'online' && pass.event?.generatePass !== false;

  return (
    <div className="modal-overlay" role="presentation">
      <div className="modal" style={{ maxWidth: '40rem', padding: 0, overflow: 'hidden' }}>
        <div className="modal-header" style={{ borderBottom: 'none', padding: '1.25rem 1.5rem' }}>
          <div className="modal-title" style={{ fontSize: 'var(--text-lg)', fontWeight: 800 }}>
            {shouldGenPass ? 'Pass Details' : 'Guest Details'}
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body" style={{ padding: '0 1.5rem 2rem 1.5rem' }}>
          {/* Dual Hero Section */}
          {shouldGenPass && (
            <div className="pass-modal-hero-grid">
              <div className="pass-hero-card-blue" style={{ justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 'var(--text-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.9, marginBottom: '0.5rem' }}>
                    Entry Code
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                    <div style={{ fontSize: '2.5rem', fontWeight: 800, letterSpacing: '0.1em', fontFamily: 'var(--font-mono)', lineHeight: 1 }}>
                      {pass.passCode}
                    </div>
                    <span className={`badge badge-${pass.status}`} style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', borderColor: 'rgba(255,255,255,0.3)', height: 'fit-content' }}>
                      {pass.status}
                    </span>
                  </div>
                </div>
                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.8, whiteSpace: 'nowrap' }}>
                  Valid for {formatEventDate(pass.event?.date, pass.event?.endDate, pass.event?.isMultiDay)} • {pass.event?.isMultiDay ? 'Single Entry per day' : 'Single Entry'}
                </div>
              </div>

              <div className="pass-hero-card-qr">
                {pass.qrCodeData ? (
                  <img src={pass.qrCodeData} alt="QR Code" />
                ) : (
                  <div style={{ color: 'var(--text-3)', fontSize: 'var(--text-xs)' }}>QR Not Available</div>
                )}
              </div>
            </div>
          )}

          {/* Guest Identity */}
          <div className="pass-section-header">Guest Identity</div>
          <div className="pass-grid-2">
            <DetailItem label="Full Name" value={displayName} />
            <DetailItem label="Email (Work)" value={pass.guestEmail} />
            <DetailItem label="Email (Personal)" value={pass.personalEmail} />
            <DetailItem label="Mobile" value={formatPhone(pass.guestPhone, pass.country)} />
            <DetailItem label="Country" value={pass.country} />
            <div className="pass-grid-col">
              <span className="pass-grid-label">Social Profile</span>
              <span className="pass-grid-value">
                {pass.linkedinId ? (
                  <a
                    href={formatLinkedIn(pass.linkedinId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--navy)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 700 }}
                  >
                    Open Profile
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                  </a>
                ) : '—'}
              </span>
            </div>
          </div>

          {/* Professional Details */}
          <div className="pass-section-header">Professional Details</div>
          <div className="pass-grid-2">
            <DetailItem label="Profession" value={pass.profession} />
            <DetailItem label="Organisation" value={pass.organization} />
            <DetailItem label="Department" value={pass.guestDepartment} />
            <DetailItem label="Industry" value={pass.industry} />
            <div className="pass-grid-col" style={{ gridColumn: 'span 2' }}>
              <span className="pass-grid-label">Office Address</span>
              <span className="pass-grid-value">{pass.address || '—'}</span>
            </div>
          </div>

          {/* Event Details */}
          <div className="pass-section-header">Event Details</div>
          <div className="pass-grid-2">
            <DetailItem label="Coordinator" value={pass.coordinatorName} />
            <DetailItem label="Event Title" value={pass.event?.title} />
            <DetailItem label="Venue" value={pass.event?.venue} />
            <DetailItem label="Event Date" value={formatEventDate(pass.event?.date, pass.event?.endDate, pass.event?.isMultiDay)} />
          </div>

          {/* Entry Verified Alert */}
          {pass.entryTime && (
            <div className="pass-verified-alert">
              <div className="pass-verified-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
              </div>
              <div className="pass-verified-content">
                <div className="pass-verified-title">Entry Verified</div>
                <div className="pass-verified-text">
                  Checked in on {new Date(pass.entryTime).toLocaleString('en-IN')}
                </div>
              </div>
            </div>
          )}

          {/* Pass Revoked Alert */}
          {pass.status === 'revoked' && (
            <div className="pass-verified-alert" style={{ background: '#fef2f2', borderColor: '#fee2e2' }}>
              <div className="pass-verified-icon" style={{ background: '#ef4444' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </div>
              <div className="pass-verified-content">
                <div className="pass-verified-title" style={{ color: '#991b1b' }}>Pass Revoked</div>
                <div className="pass-verified-text" style={{ color: '#b91c1c' }}>
                  Revoked by {pass.revokedBy?.name || 'Admin'} on {pass.revokedAt ? new Date(pass.revokedAt).toLocaleString('en-IN') : '—'}
                  <br />
                  <div style={{ marginTop: '4px' }}>
                    <strong>Reason:</strong> {pass.revocationReason || 'No reason provided'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {!readOnly ? (
          <div className="modal-footer-split">
            <div className="modal-footer-left">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => onRevoke(pass._id)}
                disabled={pass.status === 'revoked' || pass.status === 'used' || pass.status === 'expired'}
                style={{ borderRadius: 'var(--radius)', color: '#ef4444' }}
              >
                Revoke Pass
              </button>
            </div>
            <div className="modal-footer-right">
              {!shouldGenPass ? (
                <div style={{ fontSize: '0.75rem', color: '#64748b', fontStyle: 'italic', padding: '0.5rem 1rem', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                  This is an {pass.event?.eventMode === 'online' ? 'online' : 'no-pass'} event guest. No entry pass available.
                </div>
              ) : (
                <>
                  {(pass.status === 'active' || pass.status === 'pending') && (
                    <>
                      <button type="button" className="btn btn-ghost" onClick={() => downloadPassAsPDF(pass)} style={{ borderRadius: 'var(--radius)', background: '#fff' }}>
                        Download
                      </button>
                      <button type="button" className="btn btn-navy" onClick={() => onResend(pass._id)} style={{ borderRadius: 'var(--radius)', background: '#005fa7' }}>
                        Resend Email
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid var(--border-1)', padding: '1.25rem' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose} style={{ borderRadius: 'var(--radius)', minWidth: '120px' }}>
              Close
            </button>
          </div>
        )}

        {/* Hidden Printable Template */}
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <TicketPrintable pass={pass} />
        </div>
      </div>
    </div>
  );
}

export default function Passes() {
  const [passes, setPasses] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIssue, setShowIssue] = useState(false);
  const [viewPass, setViewPass] = useState(null);
  const [revokingPass, setRevokingPass] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEvent, setFilterEvent] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/passes'), api.get('/events')])
      .then(([pr, er]) => {
        setPasses(pr.data.passes || []);
        setEvents(er.data.events || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = passes.filter((p) => {
    if (filterStatus && p.status !== filterStatus) return false;
    if (filterEvent && p.event?._id !== filterEvent) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        p.guestName?.toLowerCase().includes(s) ||
        p.guestEmail?.toLowerCase().includes(s) ||
        p.personalEmail?.toLowerCase().includes(s) ||
        p.passCode?.toLowerCase().includes(s) ||
        p.country?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const handleIssued = () => {
    setShowIssue(false);
    load();
  };

  const handleResend = async (id) => {
    try {
      await api.post(`/passes/${id}/resend`);
      toast.success('Email sent!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Email failed');
    }
  };

  const handleRevoke = (passIdOrPass) => {
    // If we're passed an ID from the table, find the pass object
    const passObj = typeof passIdOrPass === 'string' ? passes.find(p => p._id === passIdOrPass) : passIdOrPass;
    setRevokingPass(passObj);
  };

  const confirmRevoke = async (id, reason) => {
    try {
      await api.delete(`/passes/${id}`, { data: { reason: reason || 'No reason provided' } });
      toast.success('Pass revoked');
      setRevokingPass(null);
      setViewPass(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to revoke pass');
    }
  };

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="page-title">Visitor passes</div>
          <div className="page-subtitle">{passes.length} total issued</div>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-navy" onClick={() => setShowIssue(true)}>
            Issue passes
          </button>
        </div>
      </div>

      <div className="page-body">
        <div className="card toolbar" style={{ marginBottom: '1rem' }}>
          <input className="toolbar-input" placeholder="Search name, emails, code, country" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select className="toolbar-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['active', 'used', 'expired', 'revoked'].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select className="toolbar-select" value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)} style={{ maxWidth: '13.75rem' }}>
            <option value="">All events</option>
            {events.map((ev) => (
              <option key={ev._id} value={ev._id}>
                {ev.title}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="page-loading">
            <div className="spinner" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-icon">Nothing to show</div>
              <div className="empty-title">{passes.length === 0 ? 'No passes yet' : 'No matches'}</div>
              {passes.length === 0 && (
                <button type="button" className="btn btn-navy" style={{ margin: '1rem auto 0', display: 'flex' }} onClick={() => setShowIssue(true)}>
                  Issue passes
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="table-wrap" style={{ overflow: 'visible' }}>
            <table>
              <thead>
                <tr>
                  <th style={{ minWidth: '180px' }}>Guest</th>
                  <th>Code</th>
                  <th style={{ minWidth: '200px' }}>Event</th>
                  <th>Country</th>
                  <th>Status</th>
                  <th>Email</th>
                  <th>Issued</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p._id} style={{ position: 'relative', zIndex: openMenuId === p._id ? 50 : 1 }}>
                    <td onClick={() => setViewPass(p)} style={{ cursor: 'pointer' }}>
                      <div className="cell-stack-title">{p.guestName}</div>
                      <div className="cell-stack-muted">{[p.guestTitle, p.firstName, p.lastName].filter(Boolean).join(' ')}</div>
                      <div className="cell-stack-muted">{p.guestEmail}</div>
                      {p.personalEmail && <div className="cell-stack-muted">{p.personalEmail}</div>}
                    </td>
                    <td onClick={() => setViewPass(p)} style={{ cursor: 'pointer' }}>
                      <span className="code-pill-table">{p.passCode}</span>
                    </td>
                    <td onClick={() => setViewPass(p)} style={{ cursor: 'pointer' }}>
                      <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--text-1)' }}>{p.event?.title}</div>
                      <div className="cell-stack-muted">
                        {formatEventDate(p.event?.date, p.event?.endDate, p.event?.isMultiDay)}
                      </div>
                      {p.event?.isMultiDay && (
                        <div style={{ fontSize: '10px', color: 'var(--navy)', fontWeight: 700, marginTop: '2px' }}>
                          Daily Entry Pass
                        </div>
                      )}
                    </td>
                    <td onClick={() => setViewPass(p)} style={{ cursor: 'pointer' }}>
                      {p.country || '—'}
                    </td>
                    <td onClick={() => setViewPass(p)} style={{ cursor: 'pointer' }}>
                      <span className={`badge badge-${p.status}`}>{p.status}</span>
                    </td>
                    <td onClick={() => setViewPass(p)} style={{ cursor: 'pointer' }}>
                      {p.emailSent ? (
                        <span style={{ color: 'var(--green)', fontSize: 'var(--text-sm)', fontWeight: 600 }}>Sent</span>
                      ) : (
                        <span style={{ color: 'var(--gray-400)', fontSize: 'var(--text-sm)' }}>Pending</span>
                      )}
                    </td>
                    <td onClick={() => setViewPass(p)} style={{ cursor: 'pointer', fontSize: 'var(--text-sm)', color: 'var(--text-3)' }}>
                      {new Date(p.createdAt).toLocaleDateString('en-IN')}
                    </td>
                    <td>
                      <div className="action-dropdown-container">
                        <button
                          type="button"
                          className={`action-dropdown-trigger ${openMenuId === p._id ? 'active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(openMenuId === p._id ? null : p._id);
                          }}
                        >
                          <MoreVerticalIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                        </button>
                        
                        {openMenuId === p._id && (
                          <>
                            <div className="action-menu-overlay" onClick={() => setOpenMenuId(null)} />
                            <div className="action-dropdown-menu">
                              <button className="action-dropdown-item" onClick={() => { setViewPass(p); setOpenMenuId(null); }}>
                                <EyeIcon style={{ width: '1rem', height: '1rem' }} />
                                View Details
                              </button>
                              <button 
                                className="action-dropdown-item" 
                                onClick={() => { handleResend(p._id); setOpenMenuId(null); }}
                                disabled={p.status === 'used' || p.status === 'expired' || p.status === 'revoked'}
                              >
                                <MailIcon style={{ width: '1rem', height: '1rem' }} />
                                Resend Invite
                              </button>
                              <div style={{ borderTop: '1px solid var(--border-1)', margin: '0.25rem 0' }} />
                              <button
                                className="action-dropdown-item danger"
                                onClick={() => { handleRevoke(p._id); setOpenMenuId(null); }}
                                disabled={p.status === 'revoked' || p.status === 'used' || p.status === 'expired'}
                              >
                                <ShieldIcon style={{ width: '1rem', height: '1rem' }} />
                                Revoke Pass
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showIssue && <IssuePassModal events={events} onClose={() => setShowIssue(false)} onIssued={handleIssued} />}
      {viewPass && <PassDetailModal pass={viewPass} onClose={() => setViewPass(null)} onResend={handleResend} onRevoke={handleRevoke} />}
      {revokingPass && (
        <RevokePassModal
          pass={revokingPass}
          onClose={() => setRevokingPass(null)}
          onRevoke={confirmRevoke}
        />
      )}
    </div>
  );
}
